import { useJsApiLoader } from '@react-google-maps/api';
import { Meteor } from 'meteor/meteor';
import { Dict } from '/imports/types';
import { formatGmapsAddress } from '/imports/addressFormat';

const libraries: any = ['geometry', 'places'];

/**
 * Loads the Google Maps JS API (geometry and places libraries) for use in React components.
 * @returns the useJsApiLoader result (isLoaded / loadError)
 */
export const useGoogleMap = () => {
  return useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: Meteor.settings.public.google.maps.apiKey,
    libraries,
    version: 'beta',
  });
};

export interface GmapsAddress {
  number?: string;
  route?: string;
  line: string;
  line2?: string;
  city: string;
  state: string;
  stateName: string;
  zipcode: string;
  country: string;
  countryName: string;
  formatted: string;
  place?: google.maps.places.PlaceResult;
  location: { lat: number; lng: number; };
}

/**
 * Maps a Google Places result into a structured GmapsAddress.
 * @param place - the resolved Google Places place
 * @returns the parsed address with components and location
 */
export const placeToAddress = (place: google.maps.places.Place) => {
  const address: any = { place };
  for (const component of place.addressComponents || []) {
    const componentType = component.types[0];
    switch (componentType) {
      case 'street_number': {
        address.number = `${component.longText}`;
        break;
      }
      case 'route': {
        address.route = `${component.longText}`;
        break;
      }
      case 'locality': {
        address.city = `${component.longText}`;
        break;
      }
      case 'administrative_area_level_1': {
        address.state = `${component.shortText}`;
        address.stateName = `${component.longText}`;
        break;
      }
      case 'postal_code': {
        address.zipcode = `${component.longText}`;
        break;
      }
      case 'country': {
        address.country = `${component.shortText}`;
        address.countryName = `${component.longText}`;
        break;
      }
    }
  }
  address.line = `${address.number} ${address.route}`;
  address.formatted = formatGmapsAddress(address);
  address.location = place.location?.toJSON();
  return address as GmapsAddress;
};

export const sanDiegoCoords = { lat: 32.732002, lng: -117.186534 };

export const destinationTypes: Dict<string> = {
  car_dealer: 'Car dealer',
  car_rental: 'Car rental',
  car_repair: 'Car repair',
  car_wash: 'Car wash',
  electric_vehicle_charging_station: 'Electric vehicle charging station',
  gas_station: 'Gas station',
  parking: 'Parking',
  rest_stop: 'Rest stop',
  corporate_office: 'Corporate office',
  farm: 'Farm',
  ranch: 'Ranch',
  art_gallery: 'Art gallery',
  art_studio: 'Art studio',
  auditorium: 'Auditorium',
  cultural_landmark: 'Cultural landmark',
  historical_place: 'Historical place',
  monument: 'Monument',
  museum: 'Museum',
  performing_arts_theater: 'Performing arts theater',
  sculpture: 'Sculpture',
  library: 'Library',
  preschool: 'Preschool',
  primary_school: 'Primary school',
  secondary_school: 'Secondary school',
  university: 'University',
  adventure_sports_center: 'Adventure sports center',
  amphitheatre: 'Amphitheatre',
  amusement_center: 'Amusement center',
  amusement_park: 'Amusement park',
  aquarium: 'Aquarium',
  banquet_hall: 'Banquet hall',
  barbecue_area: 'Barbecue area',
  botanical_garden: 'Botanical garden',
  bowling_alley: 'Bowling alley',
  casino: 'Casino',
  childrens_camp: 'Children\'s camp',
  comedy_club: 'Comedy club',
  community_center: 'Community center',
  concert_hall: 'Concert hall',
  convention_center: 'Convention center',
  cultural_center: 'Cultural center',
  cycling_park: 'Cycling park',
  dance_hall: 'Dance hall',
  dog_park: 'Dog park',
  event_venue: 'Event venue',
  ferris_wheel: 'Ferris wheel',
  garden: 'Garden',
  hiking_area: 'Hiking area',
  historical_landmark: 'Historical landmark',
  internet_cafe: 'Internet cafe',
  karaoke: 'Karaoke',
  marina: 'Marina',
  movie_rental: 'Movie rental',
  movie_theater: 'Movie theater',
  national_park: 'National park',
  night_club: 'Night club',
  observation_deck: 'Observation deck',
  off_roading_area: 'Off-roading area',
  opera_house: 'Opera house',
  park: 'Park',
  philharmonic_hall: 'Philharmonic hall',
  picnic_ground: 'Picnic ground',
  planetarium: 'Planetarium',
  plaza: 'Plaza',
  roller_coaster: 'Roller coaster',
  skateboard_park: 'Skateboard park',
  state_park: 'State park',
  tourist_attraction: 'Tourist attraction',
  video_arcade: 'Video arcade',
  visitor_center: 'Visitor center',
  water_park: 'Water park',
  wedding_venue: 'Wedding venue',
  wildlife_park: 'Wildlife park',
  wildlife_refuge: 'Wildlife refuge',
  zoo: 'Zoo',
  public_bath: 'Public bath',
  public_bathroom: 'Public bathroom',
  stable: 'Stable',
  accounting: 'Accounting',
  atm: 'Atm',
  bank: 'Bank',
  acai_shop: 'Acai shop',
  afghani_restaurant: 'Afghani restaurant',
  african_restaurant: 'African restaurant',
  american_restaurant: 'American restaurant',
  asian_restaurant: 'Asian restaurant',
  bagel_shop: 'Bagel shop',
  bakery: 'Bakery',
  bar: 'Bar',
  bar_and_grill: 'Bar and grill',
  barbecue_restaurant: 'Barbecue restaurant',
  brazilian_restaurant: 'Brazilian restaurant',
  breakfast_restaurant: 'Breakfast restaurant',
  brunch_restaurant: 'Brunch restaurant',
  buffet_restaurant: 'Buffet restaurant',
  cafe: 'Cafe',
  cafeteria: 'Cafeteria',
  candy_store: 'Candy store',
  cat_cafe: 'Cat cafe',
  chinese_restaurant: 'Chinese restaurant',
  chocolate_factory: 'Chocolate factory',
  chocolate_shop: 'Chocolate shop',
  coffee_shop: 'Coffee shop',
  confectionery: 'Confectionery',
  deli: 'Deli',
  dessert_restaurant: 'Dessert restaurant',
  dessert_shop: 'Dessert shop',
  diner: 'Diner',
  dog_cafe: 'Dog cafe',
  donut_shop: 'Donut shop',
  fast_food_restaurant: 'Fast food restaurant',
  fine_dining_restaurant: 'Fine dining restaurant',
  food_court: 'Food court',
  french_restaurant: 'French restaurant',
  greek_restaurant: 'Greek restaurant',
  hamburger_restaurant: 'Hamburger restaurant',
  ice_cream_shop: 'Ice cream shop',
  indian_restaurant: 'Indian restaurant',
  indonesian_restaurant: 'Indonesian restaurant',
  italian_restaurant: 'Italian restaurant',
  japanese_restaurant: 'Japanese restaurant',
  juice_shop: 'Juice shop',
  korean_restaurant: 'Korean restaurant',
  lebanese_restaurant: 'Lebanese restaurant',
  meal_delivery: 'Meal delivery',
  meal_takeaway: 'Meal takeaway',
  mediterranean_restaurant: 'Mediterranean restaurant',
  mexican_restaurant: 'Mexican restaurant',
  middle_eastern_restaurant: 'Middle eastern restaurant',
  pizza_restaurant: 'Pizza restaurant',
  pub: 'Pub',
  ramen_restaurant: 'Ramen restaurant',
  restaurant: 'Restaurant',
  sandwich_shop: 'Sandwich shop',
  seafood_restaurant: 'Seafood restaurant',
  spanish_restaurant: 'Spanish restaurant',
  steak_house: 'Steak house',
  sushi_restaurant: 'Sushi restaurant',
  tea_house: 'Tea house',
  thai_restaurant: 'Thai restaurant',
  turkish_restaurant: 'Turkish restaurant',
  vegan_restaurant: 'Vegan restaurant',
  vegetarian_restaurant: 'Vegetarian restaurant',
  vietnamese_restaurant: 'Vietnamese restaurant',
  wine_bar: 'Wine bar',
  administrative_area_level_1: 'Administrative area level 1',
  administrative_area_level_2: 'Administrative area level 2',
  country: 'Country',
  locality: 'Locality',
  postal_code: 'Postal code',
  school_district: 'School district',
  city_hall: 'City hall',
  courthouse: 'Courthouse',
  embassy: 'Embassy',
  fire_station: 'Fire station',
  government_office: 'Government office',
  local_government_office: 'Local government office',
  neighborhood_police_station: 'Neighborhood police station',
  police: 'Police',
  post_office: 'Post office',
  chiropractor: 'Chiropractor',
  dental_clinic: 'Dental clinic',
  dentist: 'Dentist',
  doctor: 'Doctor',
  drugstore: 'Drugstore',
  hospital: 'Hospital',
  massage: 'Massage',
  medical_lab: 'Medical lab',
  pharmacy: 'Pharmacy',
  physiotherapist: 'Physiotherapist',
  sauna: 'Sauna',
  skin_care_clinic: 'Skin care clinic',
  spa: 'Spa',
  tanning_studio: 'Tanning studio',
  wellness_center: 'Wellness center',
  yoga_studio: 'Yoga studio',
  apartment_building: 'Apartment building',
  apartment_complex: 'Apartment complex',
  condominium_complex: 'Condominium complex',
  housing_complex: 'Housing complex',
  bed_and_breakfast: 'Bed and breakfast',
  budget_japanese_inn: 'Budget japanese inn',
  campground: 'Campground',
  camping_cabin: 'Camping cabin',
  cottage: 'Cottage',
  extended_stay_hotel: 'Extended stay hotel',
  farmstay: 'Farmstay',
  guest_house: 'Guest house',
  hostel: 'Hostel',
  hotel: 'Hotel',
  inn: 'Inn',
  japanese_inn: 'Japanese inn',
  lodging: 'Lodging',
  mobile_home_park: 'Mobile home park',
  motel: 'Motel',
  private_guest_room: 'Private guest room',
  resort_hotel: 'Resort hotel',
  rv_park: 'Rv park',
  beach: 'Beach',
  church: 'Church',
  hindu_temple: 'Hindu temple',
  mosque: 'Mosque',
  synagogue: 'Synagogue',
  astrologer: 'Astrologer',
  barber_shop: 'Barber shop',
  beautician: 'Beautician',
  beauty_salon: 'Beauty salon',
  body_art_service: 'Body art service',
  catering_service: 'Catering service',
  cemetery: 'Cemetery',
  child_care_agency: 'Child care agency',
  consultant: 'Consultant',
  courier_service: 'Courier service',
  electrician: 'Electrician',
  florist: 'Florist',
  food_delivery: 'Food delivery',
  foot_care: 'Foot care',
  funeral_home: 'Funeral home',
  hair_care: 'Hair care',
  hair_salon: 'Hair salon',
  insurance_agency: 'Insurance agency',
  laundry: 'Laundry',
  lawyer: 'Lawyer',
  locksmith: 'Locksmith',
  makeup_artist: 'Makeup artist',
  moving_company: 'Moving company',
  nail_salon: 'Nail salon',
  painter: 'Painter',
  plumber: 'Plumber',
  psychic: 'Psychic',
  real_estate_agency: 'Real estate agency',
  roofing_contractor: 'Roofing contractor',
  storage: 'Storage',
  summer_camp_organizer: 'Summer camp organizer',
  tailor: 'Tailor',
  telecommunications_service_provider: 'Telecommunications service provider',
  tour_agency: 'Tour agency',
  tourist_information_center: 'Tourist information center',
  travel_agency: 'Travel agency',
  veterinary_care: 'Veterinary care',
  asian_grocery_store: 'Asian grocery store',
  auto_parts_store: 'Auto parts store',
  bicycle_store: 'Bicycle store',
  book_store: 'Book store',
  butcher_shop: 'Butcher shop',
  cell_phone_store: 'Cell phone store',
  clothing_store: 'Clothing store',
  convenience_store: 'Convenience store',
  department_store: 'Department store',
  discount_store: 'Discount store',
  electronics_store: 'Electronics store',
  food_store: 'Food store',
  furniture_store: 'Furniture store',
  gift_shop: 'Gift shop',
  grocery_store: 'Grocery store',
  hardware_store: 'Hardware store',
  home_goods_store: 'Home goods store',
  home_improvement_store: 'Home improvement store',
  jewelry_store: 'Jewelry store',
  liquor_store: 'Liquor store',
  market: 'Market',
  pet_store: 'Pet store',
  shoe_store: 'Shoe store',
  shopping_mall: 'Shopping mall',
  sporting_goods_store: 'Sporting goods store',
  store: 'Store',
  supermarket: 'Supermarket',
  warehouse_store: 'Warehouse store',
  wholesaler: 'Wholesaler',
  arena: 'Arena',
  athletic_field: 'Athletic field',
  fishing_charter: 'Fishing charter',
  fishing_pond: 'Fishing pond',
  fitness_center: 'Fitness center',
  golf_course: 'Golf course',
  gym: 'Gym',
  ice_skating_rink: 'Ice skating rink',
  playground: 'Playground',
  ski_resort: 'Ski resort',
  sports_activity_location: 'Sports activity location',
  sports_club: 'Sports club',
  sports_coaching: 'Sports coaching',
  sports_complex: 'Sports complex',
  stadium: 'Stadium',
  swimming_pool: 'Swimming pool',
  airport: 'Airport',
  airstrip: 'Airstrip',
  bus_station: 'Bus station',
  bus_stop: 'Bus stop',
  ferry_terminal: 'Ferry terminal',
  heliport: 'Heliport',
  international_airport: 'International airport',
  light_rail_station: 'Light rail station',
  park_and_ride: 'Park and ride',
  subway_station: 'Subway station',
  taxi_stand: 'Taxi stand',
  train_station: 'Train station',
  transit_depot: 'Transit depot',
  transit_station: 'Transit station',
  truck_stop: 'Truck stop'
};